import React, { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { ThemeOptions } from "@mui/material/styles";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useParams } from "react-router-dom";
import ReactDOM from "react-dom/client";

import Loading from "./components/Loading";
import ScrollToTop from "./components/ScrollToTop";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CountriesProvider } from "./contexts/CountriesContext";
import { TimeZoneProvider } from "./contexts/TimeZoneContext";
import { LanguagesProvider } from "./contexts/LanguagesContext";
import { NumericConventionProvider } from "./contexts/NumericConventionContext";
import { UiPreferencesProvider } from "./contexts/UiPreferencesContext";
import { WebSocketContextProvider } from "./contexts/WebSocketContext";
import { wrapWith, Wrapper } from "./utils/wrapWith";
import AuthCallback from "./AuthCallback";
import store from "./store";
import routes from "./routes"; // Make sure RouteConfig is defined if needed

// Styles
import "../scss/style.scss";

// i18n
import "./i18n";

const Page404 = lazy(() => import("./views/Page404"));
const Page500 = lazy(() => import("./views/Page500"));

// Register Service Worker
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        console.log("SW registered: ", registration);
      })
      .catch((registrationError) => {
        console.log("SW registration failed: ", registrationError);
      });
  });
}

interface LayoutWithWrappersProps {
  layout: React.ComponentType<any>;
  mainElement: React.ComponentType<any> | React.LazyExoticComponent<React.ComponentType<any>>;
  asideElement?: React.ComponentType<any> | React.LazyExoticComponent<React.ComponentType<any>>;
  wrappers?: Wrapper[] | ((params: Record<string, string>) => Wrapper[]);
}

const LayoutWithWrappers = ({ layout: Layout, mainElement, asideElement, wrappers }: LayoutWithWrappersProps) => {
  const params = useParams() as Record<string, string>;
  const resolvedWrappers = typeof wrappers === "function" ? wrappers(params) : (wrappers ?? []);
  const content = (<Layout mainElement={mainElement} asideElement={asideElement} />);
  return <>{wrapWith(resolvedWrappers, content)}</>;
};

interface RouteWithLayoutProps {
  path: string;
  layout: React.LazyExoticComponent<React.ComponentType<any>>;
  mainElement: React.ComponentType<any> | React.LazyExoticComponent<React.ComponentType<any>>;
  asideElement?: React.ComponentType<any> | React.LazyExoticComponent<React.ComponentType<any>>;
  darkThemeOptions: ThemeOptions;
  lightThemeOptions: ThemeOptions;
  wrappers?: Array<React.FC<{ children: React.ReactNode }>> | ((params: Record<string, string>) => Array<React.FC<{ children: React.ReactNode }>>);
}

const routeWithLayout = ({
  path,
  mainElement,
  asideElement,
  layout: Layout,
  darkThemeOptions,
  lightThemeOptions,
  wrappers = [],
}: RouteWithLayoutProps): React.ReactElement => {
  return (
    <Route
      key={path}
      path={path}
      element={
        <ThemeProvider darkThemeOptions={darkThemeOptions} lightThemeOptions={lightThemeOptions}>
          <Suspense fallback={<Loading />}>
            <LayoutWithWrappers
              layout={Layout}
              mainElement={mainElement}
              asideElement={asideElement}
              wrappers={wrappers}
            />
          </Suspense>
        </ThemeProvider >
      }
    />
  );
};

const root = ReactDOM.createRoot(document.getElementById("root")!);
root.render(
  <Provider store={store}>
    <CountriesProvider>
      <TimeZoneProvider>
        <LanguagesProvider>
          <NumericConventionProvider>
            <UiPreferencesProvider>
              <WebSocketContextProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <Routes>
                    {routes.map((route) => routeWithLayout(route))}
                    <Route path="/auth-callback" element={<AuthCallback />} />
                    <Route path="/noticias" element={<Navigate to="/home/following" replace />} />
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/500" element={<Page500 />} />
                    <Route path="/404" element={<Page404 />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </BrowserRouter>
              </WebSocketContextProvider>
            </UiPreferencesProvider>
          </NumericConventionProvider>
        </LanguagesProvider>
      </TimeZoneProvider>
    </CountriesProvider>
  </Provider>
);